import React from 'react';
import { Route, Switch, useHistory, useLocation } from 'react-router-dom';
import { PATHS } from 'src/common/constants';
import TabSwitch, { TabSwitchTab } from 'src/components/TabSwitch/TabSwitch';
import FontAwesomeIcon from 'src/components/Icon';
import CalcomIntegrationTabSkeleton from './skeletons/CalcomIntegrationTabSkeleton';
import {
  IUseCalcomConnection,
  useCalcomOauthConnection,
} from './CustomHooks/useCalcomOauthConnection';
import CalcomConnectCard from './components/CalcomConnectCard/CalcomConnectCard';
import CalcomConnectedCard from './components/CalcomConnectedCard/CalcomConnectedCard';
import ConnectedCalendars from './components/ConnectedCalendars/ConnectedCalendars';
import BookingLinksManager from './components/BookingLinksManager/BookingLinksManager';
import KeyPickupsManager from './components/KeyPickupsManager/KeyPickupsManager';
import './calcom-integration-tab.scss';

const CALCOM_TABS: TabSwitchTab[] = [
  {
    id: 'connection',
    label: 'My Connected Calendars',
    icon: <FontAwesomeIcon glyph="calendar-alt" weight="solid" />,
  },
  {
    id: 'bookings',
    label: 'Manage Booking Links',
    icon: <FontAwesomeIcon glyph="link" weight="solid" />,
  },
  {
    id: 'key-pickups',
    label: 'Schedule Key Pickups',
    icon: <FontAwesomeIcon glyph="key" weight="solid" />,
  },
];

const resolveActiveTabId = (pathname: string): string => {
  if (pathname.startsWith(PATHS.CALENDAR_CALCOM_BOOKINGS)) {
    return 'bookings';
  }

  if (pathname.startsWith(PATHS.CALENDAR_CALCOM_KEY_PICKUPS)) {
    return 'key-pickups';
  }

  return 'connection';
};

export const CalcomIntegrationTab = () => {
  const history = useHistory();
  const location = useLocation();

  const {
    status,
    isConnected,
    isLoadingStatus,
    statusError,
    isConnecting,
    connectError,
    connectViaFullPageRedirect,
    connectedCalendars,
    conflictingIntegration,
    disconnect,
  }: IUseCalcomConnection = useCalcomOauthConnection();

  const activeTabId: string = resolveActiveTabId(location.pathname);

  const goToTab = (tabId: string): void => {
    if (tabId === 'bookings') {
      history.push(PATHS.CALENDAR_CALCOM_BOOKINGS);
      return;
    }

    if (tabId === 'key-pickups') {
      history.push(PATHS.CALENDAR_CALCOM_KEY_PICKUPS);
      return;
    }

    history.push(PATHS.CALENDAR_CALCOM);
  };

  if (isLoadingStatus) {
    return <CalcomIntegrationTabSkeleton />;
  }

  if (statusError) {
    return (
      <div className="calcom-integration-tab">
        <p className="error-text">{statusError}</p>
      </div>
    );
  }

  if (!isConnected) {
    return (
      <div className="calcom-integration-tab">
        <CalcomConnectCard
          isConnecting={isConnecting}
          connectError={connectError}
          conflictingIntegration={conflictingIntegration}
          onConnect={connectViaFullPageRedirect}
        />
      </div>
    );
  }

  return (
    <div className="calcom-integration-tab">
      <div className="calcom-tab-shell">
        <CalcomConnectedCard
          username={status?.calcomUsername ?? null}
          onDisconnect={disconnect}
        />
        <TabSwitch
          tabs={CALCOM_TABS}
          activeTabId={activeTabId}
          onTabChange={goToTab}
        />
        <Switch>
          <Route
            path={[
              `${PATHS.CALENDAR_CALCOM_BOOKINGS}/:propertyId`,
              PATHS.CALENDAR_CALCOM_BOOKINGS,
            ]}
          >
            <div className="card card-wide">
              <BookingLinksManager />
            </div>
          </Route>
          <Route path={PATHS.CALENDAR_CALCOM_KEY_PICKUPS}>
            <div className="card card-wide">
              <KeyPickupsManager />
            </div>
          </Route>
          <Route path={PATHS.CALENDAR_CALCOM}>
            <div className="card card-wide">
              <ConnectedCalendars calendars={connectedCalendars} />
            </div>
          </Route>
        </Switch>
      </div>
    </div>
  );
};

export default CalcomIntegrationTab;